import { get } from 'lodash';
import Vue from 'vue';

import ApiService from '@/core/services/api.service';
import {
  SALES_REPORT_DEFAULT_PAGE_SIZE,
  SALES_REPORT_MAX_PAGE_SIZE,
} from '../constants';
import { downloadBlobFile } from '@/utils/common';
import { formatDBDate, getCurrentDate } from '@/utils/date';
import { searchCustomers } from '@/utils/customerSearch';

const API_PREFIX = '/v1/sales-reports';

const unwrapData = (response, fallback = {}) => {
  return get(response, 'data.data', fallback);
};

const compactParams = (params = {}) => {
  return Object.keys(params).reduce((acc, key) => {
    const value = params[key];
    if (value === undefined || value === null || value === '') {
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});
};

const normalizeStoreIds = (storeIds = []) => {
  if (!Array.isArray(storeIds) || !storeIds.length) {
    return undefined;
  }
  const ids = storeIds
    .map((store) => {
      if (typeof store === 'object' && store !== null) {
        return store.id;
      }
      return store;
    })
    .filter((id) => id !== undefined && id !== null && id !== '');
  return ids.length ? ids.join(',') : undefined;
};

/** PMM: danh sách ID dạng CSV (billTypes, brandIds, categoryIds, providerIds). */
const normalizeIdList = (items = []) => {
  if (!Array.isArray(items) || !items.length) {
    return undefined;
  }
  const ids = items
    .map((item) => {
      if (typeof item === 'object' && item !== null) {
        return item.id;
      }
      return item;
    })
    .filter((id) => id !== undefined && id !== null && id !== '');
  return ids.length ? ids.join(',') : undefined;
};

const tabEndpoint = (tab) => {
  if (tab.endpoint) {
    return tab.endpoint;
  }
  return (tab.apiPath || '').replace(/^\/?sales-reports\//, '');
};

/**
 * Tab thường: /v1/sales-reports/<endpoint>.
 * Tab có prefix khác (vd `purchase-reports/by-imei`): /v1/<endpoint>.
 */
const resolveTabBaseUrl = (tab) => {
  if (tab.endpoint) {
    const prefix = tab.apiPrefix || '/v1';
    return `${prefix}/${tab.endpoint}`.replace(/\/+/g, '/');
  }
  return `${API_PREFIX}/${tabEndpoint(tab)}`;
};

const parseFilenameFromDisposition = (header) => {
  if (!header) {
    return null;
  }
  const utf8Match = header.match(/filename\*=UTF-8''([^;\n]+)/i);
  if (utf8Match) {
    return decodeURIComponent(utf8Match[1]);
  }
  const quotedMatch = header.match(/filename="([^"]+)"/i);
  if (quotedMatch) {
    return quotedMatch[1];
  }
  const plainMatch = header.match(/filename=([^;\n]+)/i);
  if (plainMatch) {
    return plainMatch[1].trim();
  }
  return null;
};

/** API: truyền cả startDate + endDate hoặc bỏ cả hai (sales-reports-fe-api.md §3). */
const appendOptionalDateRange = (query, filters) => {
  const startDate = formatDBDate(filters.startDate);
  const endDate = formatDBDate(filters.endDate);
  if (startDate && endDate) {
    query.startDate = startDate;
    query.endDate = endDate;
  }
  return query;
};

const buildCommonFilterParams = (filters = {}, options = {}) => {
  const {
    requiresDate = true,
    paginated = false,
    page = 1,
    limit,
  } = options;
  const query = {};

  if (requiresDate) {
    appendOptionalDateRange(query, filters);
  }

  if (filters.storeId) {
    query.storeId = filters.storeId;
  }

  const storeIds = normalizeStoreIds(filters.storeIds);
  if (storeIds) {
    query.storeIds = storeIds;
  }

  if (paginated) {
    query.page = page;
    query.limit = limit;
  }

  return query;
};

/** PMM bộ lọc nâng cao → query params theo tab (pmm-reports-fe-api.md §2, §5.2). */
const appendAdvancedFilterParams = (query, filters = {}, tab = {}) => {
  const advancedFields = tab.advancedFields || [];
  const filterFields = tab.filterFields || [];

  if (filterFields.includes('smartSearch') && filters.productSearch) {
    query.productSearch = filters.productSearch;
  }
  const useMultiAdvanced = Boolean(tab.multiSelectAdvanced);

  if (advancedFields.includes('billType')) {
    if (useMultiAdvanced) {
      const billTypes = normalizeIdList(filters.billTypes);
      if (billTypes) {
        query.billTypes = billTypes;
      }
    } else if (filters.billType) {
      query.billType = filters.billType;
    }
  }
  if (advancedFields.includes('brand')) {
    if (useMultiAdvanced) {
      const brandIds = normalizeIdList(filters.brandIds);
      if (brandIds) {
        query.brandIds = brandIds;
      }
    } else if (filters.brandId) {
      query.brandId = filters.brandId;
    }
  }
  // Cây danh mục: BE nhận categoryIds (CSV) và tự mở rộng con (pmm §2.4).
  if (advancedFields.includes('category')) {
    if (useMultiAdvanced) {
      const categoryIds = normalizeIdList(filters.categoryIds);
      if (categoryIds) {
        query.categoryIds = categoryIds;
      }
    } else if (filters.categoryId) {
      query.categoryId = filters.categoryId;
      query.categoryIds = filters.categoryId;
    }
  }
  if (advancedFields.includes('provider')) {
    if (useMultiAdvanced) {
      const providerIds = normalizeIdList(filters.providerIds);
      if (providerIds) {
        query.providerIds = providerIds;
      }
    } else if (filters.providerId) {
      if (tab.key === 'purchases') {
        query.providerId = filters.providerId;
      } else {
        query.providerIds = filters.providerId;
      }
    }
  }
  if (advancedFields.includes('subProvider')) {
    const subProviderIds = normalizeIdList(filters.subProviderIds);
    if (subProviderIds) {
      query.subProviderIds = subProviderIds;
    }
  }
  if (advancedFields.includes('customer') && filters.customerId) {
    query.customerId = filters.customerId;
  }
  if (advancedFields.includes('spc') && filters.spc) {
    query.spc = filters.spc;
  }
  return query;
};

const buildTabQuery = (tab, filters, pagination = null) => {
  const isInventoryReport = tab.key === 'inventory-value-by-store';
  const page = pagination?.page || 1;
  const limit = Math.min(
    Number(pagination?.limit) || SALES_REPORT_DEFAULT_PAGE_SIZE,
    SALES_REPORT_MAX_PAGE_SIZE,
  );
  const query = buildCommonFilterParams(filters, {
    requiresDate: !isInventoryReport,
    paginated: Boolean(tab.paginated),
    page,
    limit,
  });

  if (tab.filterMode === 'advanced') {
    appendAdvancedFilterParams(query, filters, tab);
  }

  if (tab.key === 'by-bill') {
    query.billType = filters.billType;
  }

  if (tab.key === 'by-imei') {
    query.imeiCode = filters.imeiCode;
    query.productId = filters.productId;
  }

  if (filters.searchText) {
    if (tab.key === 'by-bill') {
      query.billNumber = filters.searchText;
    } else if (tab.key === 'inventory-value-by-store') {
      query.keyword = filters.searchText;
    }
  }

  return query;
};

const defaultExportFilename = (tabKey) =>
  `bao_cao_ban_hang_${tabKey}_${getCurrentDate('YYYYMMDD_HHmmss')}.xlsx`;

const normalizePagination = (data, requestPagination = {}) => {
  const apiPagination = data.pagination || {};
  const limit = Math.min(
    Number(apiPagination.limit || apiPagination.pageSize || requestPagination.limit) ||
      SALES_REPORT_DEFAULT_PAGE_SIZE,
    SALES_REPORT_MAX_PAGE_SIZE,
  );
  const page = Number(apiPagination.page || requestPagination.page) || 1;
  let totalItems = 0;
  if (apiPagination.totalItems != null) {
    totalItems = Number(apiPagination.totalItems);
  } else if (typeof data.totalRow === 'number') {
    totalItems = Number(data.totalRow);
  }
  const totalPage = Number(
    apiPagination.totalPage ||
      apiPagination.totalPages ||
      (limit > 0 ? Math.ceil(totalItems / limit) : 1),
  );

  return {
    page,
    limit,
    totalItems,
    totalPage: Math.max(totalPage, 1),
  };
};

const downloadBlobResponse = async (response, fallbackFilename) => {
  const contentType = String(response.headers['content-type'] || '');
  if (contentType.includes('application/json')) {
    const text = await response.data.text();
    let message = 'Lỗi xuất Excel';
    try {
      const json = JSON.parse(text);
      message = json.message || message;
    } catch (e) {
      // keep default message
    }
    throw new Error(message);
  }
  const filename =
    parseFilenameFromDisposition(response.headers['content-disposition']) ||
    fallbackFilename;
  downloadBlobFile(response.data, filename);
};

const SalesReportsService = {
  async fetchStores() {
    const response = await ApiService.get('/stores/getStores');
    return get(response, 'data.data', []);
  },

  /**
   * Option cho bộ lọc nâng cao PMM (brand / DM cấp 1).
   * NCC không preload nữa — dùng searchProviders() để tìm server-side.
   */
  async fetchFilterOptions() {
    const [brandsRes, categoriesRes] = await Promise.allSettled([
      ApiService.query('brands', { params: { page: 1, pageSize: 1000, name: '' } }),
      ApiService.query('categories', { params: { page: 1, limit: 1000 } }),
    ]);

    const pick = (res, path) =>
      res.status === 'fulfilled' ? get(res.value, path, []) || [] : [];

    return {
      brands: pick(brandsRes, 'data.data.brands').map((b) => ({
        id: b.id,
        name: b.name,
      })),
      categories: pick(categoriesRes, 'data.data.list_caterogy')
        .filter((c) => c.level === 1 || !c.parentId)
        .map((c) => ({
          id: c.id,
          name: c.name,
        })),
      providers: [],
    };
  },

  /**
   * Tìm nhà cung cấp server-side theo từ khóa (tên/mã).
   *
   * QUAN TRỌNG: NCC đã được migrate sang `customer` — luôn lấy NCC từ
   * `v1/customers` (qua searchCustomers), KHÔNG dùng `v1/providers` nữa.
   * Trả về [] khi từ khóa rỗng (đặc tính của searchCustomers).
   */
  async searchProviders(keyword = '', size = 20) {
    const items = await searchCustomers(keyword, size);
    return items.map((c) => ({
      id: c.id,
      name: c.fullName || c.name || '',
      code: c.code || c.customerCode || '',
    }));
  },

  /**
   * Tìm NCC phụ server-side — onlyChild khi chưa chọn NCC chính, parentIds khi đã chọn.
   */
  async searchSubProviders(keyword = '', size = 20, providerIds = []) {
    const normalizedIds = (Array.isArray(providerIds) ? providerIds : [])
      .map((item) => (typeof item === 'object' && item !== null ? item.id : item))
      .filter((id) => id !== undefined && id !== null && id !== '');
    const extraParams = normalizedIds.length
      ? { parentIds: normalizedIds.join(',') }
      : { onlyChild: true };
    const items = await searchCustomers(keyword, size, false, extraParams);
    return items.map((c) => ({
      id: c.id,
      name: c.fullName || c.name || '',
      code: c.code || c.customerCode || '',
    }));
  },

  async fetchTabData(tab, filters, pagination) {
    const query = buildTabQuery(tab, filters, pagination);

    const response = await ApiService.query(resolveTabBaseUrl(tab), {
      params: compactParams(query),
    });
    const data = unwrapData(response, {});
    const normalizedPagination = tab.paginated
      ? normalizePagination(data, pagination)
      : null;

    return {
      rows: data.rows || [],
      totalRow: data.totalRow || null,
      storeCount: data.storeCount ?? 0,
      storeNameA: data.storeNameA || null,
      storeNameB: data.storeNameB || null,
      pagination: normalizedPagination,
      summary: data.summary || null,
      totals: data.totals || null,
      total: data.total || null,
      revenueByDay: data.revenueByDay || null,
    };
  },

  // Tổng theo bộ lọc cho tab có summaryEndpoint riêng — không chứa pagination, không lặp lại khi phân trang
  async fetchTabSummary(tab, filters) {
    if (!tab?.summaryEndpoint) return null;
    const query = buildTabQuery(tab, filters, null);
    const prefix = tab.apiPrefix || '/v1';
    const url = `${prefix}/${tab.summaryEndpoint}`.replace(/\/+/g, '/');
    const response = await ApiService.query(url, { params: compactParams(query) });
    return unwrapData(response, null);
  },

  async exportTabExcel(tab, filters) {
    if (!tab || tab.key === 'overview') {
      throw new Error('Tab này không hỗ trợ xuất Excel.');
    }

    const query = buildTabQuery(tab, filters, null);
    const response = await ApiService.get(`${resolveTabBaseUrl(tab)}/export`, {
      params: compactParams(query),
      responseType: 'blob',
    });

    await downloadBlobResponse(response, defaultExportFilename(tab.key));
  },

  /** Check IMEI hàng loạt — POST /v1/imei-reports/bulk-check (pmm-reports-fe-api.md §4.2). */
  async bulkCheckImei(imeis) {
    const response = await ApiService.post('/v1/imei-reports/bulk-check', { imeis });
    return unwrapData(response, {});
  },

  async bulkCheckImeiExport(imeis) {
    const response = await Vue.axios.post(
      '/v1/imei-reports/bulk-check/export',
      { imeis },
      { responseType: 'blob' },
    );
    await downloadBlobResponse(
      response,
      `check_imei_hang_loat_${getCurrentDate('YYYYMMDD_HHmmss')}.xlsx`,
    );
  },

  async fetchOverview(filters) {
    const params = compactParams({
      storeId: filters.storeId,
      storeIds: normalizeStoreIds(filters.storeIds),
    });
    appendOptionalDateRange(params, filters);

    const [billTypeResponse, inventoryResponse, byProductResponse] = await Promise.all([
      ApiService.query(`${API_PREFIX}/by-bill-type`, { params }),
      ApiService.query(`${API_PREFIX}/inventory-value-by-store`, {
        params: compactParams({ storeIds: params.storeIds }),
      }),
      ApiService.query(`${API_PREFIX}/by-product`, {
        params: {
          ...params,
          page: 1,
          limit: 1,
        },
      }),
    ]);

    const billTypeData = unwrapData(billTypeResponse, {});
    const inventoryData = unwrapData(inventoryResponse, {});
    const byProductData = unwrapData(byProductResponse, {});
    const revenue = get(billTypeData, 'totals.doanhThu', 0);
    const grossProfit = get(billTypeData, 'totals.laiGop', 0);

    return {
      revenue,
      grossProfit,
      margin: revenue > 0 ? grossProfit / revenue : 0,
      inventoryValue: get(inventoryData, 'total.tongGiaTriTonKho', 0),
      skuCount:
        get(byProductData, 'summary.slSanPham', null) ??
        get(byProductData, 'pagination.totalItems', 0),
    };
  },
};

export default SalesReportsService;
